/**
 * Reusable category menu component
 * Displays a grid of feature cards for category pages
 */

import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@saiens/shared/utils';

export interface CategoryMenuItem {
  id: string;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  route: string | null;
  bgColor?: string;
  textColor?: string;
}

interface CategoryMenuProps {
  title: string;
  description: string;
  items: CategoryMenuItem[];
}

export function CategoryMenu({ title, description, items }: CategoryMenuProps) {
  return (
    <div className="px-4 py-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">{title}</h1>
        <p className="text-sm text-gray-500">{description}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {items.map((item) => {
          const Icon = item.icon;

          if (!item.route) {
            return (
              <div
                key={item.id}
                className="relative rounded-xl bg-gray-50 p-4 opacity-60"
              >
                <span className="absolute right-2 top-2 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] text-gray-500">
                  即將推出
                </span>
                <div className="mb-2 inline-flex rounded-lg bg-gray-100 p-2 text-gray-400">
                  <Icon className="h-6 w-6" />
                </div>
                <p className="font-medium text-gray-400">{item.label}</p>
                <p className="text-xs text-gray-400">{item.description}</p>
              </div>
            );
          }

          return (
            <Link
              key={item.id}
              to={item.route}
              className="rounded-xl bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <div
                className={cn(
                  'mb-2 inline-flex rounded-lg p-2',
                  item.bgColor || 'bg-blue-50',
                  item.textColor || 'text-blue-600',
                )}
              >
                <Icon className="h-6 w-6" />
              </div>
              <p className="font-medium text-gray-900">{item.label}</p>
              <p className="text-xs text-gray-500">{item.description}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
